import { Asset } from 'app-lib/assetUtils'
import { DEX } from 'app-lib/dexSelector/types'
import { ProviderType } from 'bzx-common/src/domain/ProviderTypeDictionary'
import { BigNumber } from '@0x/utils'
import { getBaseQuote, IChainConfig, udfFeeds } from './chainCommon'

const assets = [
  Asset.AAVE,
  Asset.APE,
  Asset.COMP,
  Asset.DAI,
  Asset.ETH,
  Asset.LINK,
  Asset.LRC,
  Asset.MKR,
  Asset.OOKI,
  Asset.UNI,
  Asset.USDC,
  Asset.USDT,
  Asset.WBTC,
  Asset.YFI,
]

/**
 * Pairs and tokens that shouldnt be listed, even if they appear in the protocol:
 * * KNC (due to an upgrade)
 * * OOKI (no price feed)
 * * BZRX
 */
const tradePairs: [Asset, Asset][] = [
  [Asset.AAVE, Asset.APE],
  [Asset.AAVE, Asset.DAI],
  [Asset.AAVE, Asset.ETH],
  [Asset.AAVE, Asset.LINK],
  [Asset.AAVE, Asset.UNI],
  [Asset.AAVE, Asset.USDC],
  [Asset.AAVE, Asset.USDT],
  [Asset.AAVE, Asset.WBTC],
  [Asset.APE, Asset.DAI],
  [Asset.APE, Asset.ETH],
  [Asset.APE, Asset.LINK],
  [Asset.APE, Asset.UNI],
  [Asset.APE, Asset.USDC],
  [Asset.APE, Asset.USDT],
  [Asset.APE, Asset.WBTC],
  [Asset.COMP, Asset.DAI],
  [Asset.COMP, Asset.ETH],
  [Asset.COMP, Asset.LINK],
  [Asset.COMP, Asset.UNI],
  [Asset.COMP, Asset.USDC],
  [Asset.COMP, Asset.USDT],
  [Asset.COMP, Asset.WBTC],
  [Asset.ETH, Asset.DAI],
  [Asset.ETH, Asset.USDC],
  [Asset.ETH, Asset.USDT],
  [Asset.ETH, Asset.WBTC],
  [Asset.LINK, Asset.DAI],
  [Asset.LINK, Asset.ETH],
  [Asset.LINK, Asset.UNI],
  [Asset.LINK, Asset.USDC],
  [Asset.LINK, Asset.USDT],
  [Asset.LINK, Asset.WBTC],
  [Asset.LRC, Asset.DAI],
  [Asset.LRC, Asset.ETH],
  [Asset.LRC, Asset.UNI],
  [Asset.LRC, Asset.USDC],
  [Asset.LRC, Asset.USDT],
  [Asset.LRC, Asset.WBTC],
  [Asset.MKR, Asset.DAI],
  [Asset.MKR, Asset.ETH],
  [Asset.MKR, Asset.UNI],
  [Asset.MKR, Asset.USDC],
  [Asset.MKR, Asset.USDT],
  [Asset.MKR, Asset.WBTC],
  [Asset.UNI, Asset.DAI],
  [Asset.UNI, Asset.ETH],
  [Asset.UNI, Asset.USDC],
  [Asset.UNI, Asset.USDT],
  [Asset.UNI, Asset.WBTC],
  [Asset.USDC, Asset.DAI],
  [Asset.USDC, Asset.USDT],
  [Asset.USDT, Asset.DAI],
  [Asset.WBTC, Asset.DAI],
  [Asset.WBTC, Asset.USDC],
  [Asset.WBTC, Asset.USDT],
  [Asset.YFI, Asset.DAI],
  [Asset.YFI, Asset.ETH],
  [Asset.YFI, Asset.USDC],
  [Asset.YFI, Asset.USDT],
  [Asset.YFI, Asset.WBTC],
]

// OOKI can not be borrowed or used as collateral (no price feed)
const borrowable = assets.filter((asset) => asset !== Asset.OOKI)
// iETH can not be used as collateral because there is an issue with pricefeed. Ask solidity devs.
const collateralAssets = borrowable.concat(borrowable.map((asset) => ('I' + asset) as Asset))
// wstETH can be used as collateral
collateralAssets.push(Asset.WSTETH)

const config: IChainConfig = {
  chainDetails: {
    chainId: 1,
    chainName: 'Ethereum Mainnet',
    shortName: 'Ethereum',
    blockExplorer: 'https://etherscan.io',
    blocksPerYear: 0, // TODO
    nativeCurrency: {
      name: 'ETH',
      symbol: 'ETH',
      decimals: 18,
    },
  },
  borrowing: { assets: borrowable, collateralAssets },
  comingSoon: {
    tokens: {},
  },
  contracts: {
    bzx: '0xD8Ee69652E4e4838f2531732a46d1f7F584F0b7f',
    helperProxy: '0xb887f5b81deec1e271b06257f138e5a9d422bc8c',
    priceFeed: '0x09Ef93750C5F33ab469851F022C1C42056a8BAda',
    sweepFees: '0xfFB328AD3b727830F9482845A4737AfDDDe85554',
    tokenRegistry: '0xf0E474592B455579Fe580D610b846BdBb529C6F7',
  },
  dexes: [DEX.SUSHI, DEX.UNISWAPV3],
  dexesDetails: {
    [DEX.SUSHI]: { id: 1, address: '0x2f9bBB09032aE0957d5d575Fdf8dd2c5c7C2A721' },
    [DEX.UNISWAPV3]: { id: 2, address: '0x1bffc51F229C6C51f9593F24728DC2271BB21d91' },
  },
  features: { dexSelector: true },
  gas: {
    priceDefault: new BigNumber(2).times(10 ** 9),
    amount: { coefficient: 1.2, fallback: 450000 },
  },
  lending: { assets },
  trading: {
    defaultPair: { baseToken: Asset.ETH, quoteToken: Asset.USDT },
    pairs: tradePairs,
    baseQuote: getBaseQuote(tradePairs),
    pairConfigs: {},
  },
  native: { asset: Asset.ETH, wrapped: Asset.WETH },
  nativeTokenSupported: true,
  compromisedContracts: [],
  icon: { name: 'ethereum-logo', color: '#607bff' },
  chartUDF: udfFeeds.BINANCE,
  protocol: { version: 4 },
  stableCoins: [Asset.USDC, Asset.USDT, Asset.DAI],
  rpcUrlDefault: process.env.RPC_URL_ETH,
  stablecoinForUsdRates: Asset.DAI,
  stats: { assets: assets.filter((asset) => asset !== Asset.OOKI) },
  twitterHandle: '#ethereum',
  uiDisabled: false,
  uiDisabledReason: '',
  wallets: [
    ProviderType.MetaMask,
    ProviderType.TrustWallet,
    ProviderType.WalletConnect,
    ProviderType.WalletLink,
  ],
  uniRouteSettings: {},
}

export const stakingConfig = {
  stakeableAssets: [Asset.OOKI, Asset.IOOKI, Asset.vBZRX, Asset.SLP_OOKI_ETH],
  stakingContract: '0x16f179f5c344cc29672a58ea327a26f64b941a63',
  voteDelegatorContract: '0xEA936212fE4f3A69d0E8eCF9a2a35D6c1f8D2c89',
  voteDelegatorUrl: 'https://etherscan.io/address/0xEA936212fE4f3A69d0E8eCF9a2a35D6c1f8D2c89',
  masterChefSushiV2Contract: '0xef0881ec094552b2e128cf945ef17a6752b4ec5d',
  sushiPoolIdSLP: 50,
}

export const treasuryAddress = '0xfedc4dd5247b93feb41e899a09c44cfabec29cbc'
export const feesReceiver = '0xcbDE8C5603D4bA855a162A450B1d054A02D8448f'
export const daoConfig = {
  daoContract: '0x3133b4f4dcffc083724435784fefad510fa659c6',
}

export default config
